import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewChild,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { ButtonModule } from 'primeng/button';
import { filter } from 'rxjs';
import { NotificationsStore } from '@/app/core/notifications/notifications.store';
import {
  NOTIFICATIONS_INBOX_ROUTE,
  kindIcon,
  kindLabel,
  resolveNotificationRoute,
} from '@/app/core/notifications/notifications-navigation.util';
import { UserNotification } from '@/app/core/notifications/notifications.types';

@Component({
  selector: 'app-notifications-bell',
  standalone: true,
  imports: [CommonModule, OverlayPanelModule, ButtonModule],
  templateUrl: './notifications-bell.component.html',
  styleUrl: './notifications-bell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsBellComponent {
  /** `topbar` = barra interna de sistemas; `sections` = landing de selección. */
  readonly variant = input<'topbar' | 'sections'>('topbar');

  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  protected readonly store = inject(NotificationsStore);

  @ViewChild('panel') private panel?: OverlayPanel;

  protected readonly panelOpen = signal(false);

  protected readonly previewItems = computed(() => this.store.unreadItems().slice(0, 8));
  protected readonly unreadBadge = computed(() => {
    const count = this.store.unread();
    if (count <= 0) return null;
    return count > 99 ? '99+' : String(count);
  });

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        void this.store.refreshUnreadCount();
      });

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.destroyRef.onDestroy(() => {
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
      });
    }
  }

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      void this.store.refreshUnreadCount();
    }
  };

  protected togglePanel(event: Event): void {
    this.panel?.toggle(event);
    this.panelOpen.set(!this.panelOpen());
    void this.store.refresh({ limit: 25, silent: true });
  }

  protected openInbox(): void {
    this.panel?.hide();
    this.panelOpen.set(false);
    void this.router.navigateByUrl(NOTIFICATIONS_INBOX_ROUTE);
  }

  protected async openNotification(event: Event, item: UserNotification): Promise<void> {
    event.stopPropagation();
    if (!item.read) {
      await this.store.markRead(item.id);
    }
    this.panel?.hide();
    this.panelOpen.set(false);
    const target = resolveNotificationRoute(item);
    if (target) {
      void this.router.navigateByUrl(target);
      return;
    }
    void this.router.navigateByUrl(NOTIFICATIONS_INBOX_ROUTE);
  }

  protected async markAllRead(event: Event): Promise<void> {
    event.stopPropagation();
    try {
      await this.store.markAllRead();
    } catch {
      // El store ya setea error en refresh si falla la API.
    }
  }

  protected relativeTime(value: string): string {
    const created = new Date(value).getTime();
    const diffMs = Date.now() - created;
    const minutes = Math.floor(diffMs / 60_000);
    if (minutes < 1) return 'Ahora';
    if (minutes < 60) return `Hace ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Hace ${hours} h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `Hace ${days} d`;
    return new Date(value).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
  }

  protected labelForKind(kind: string): string {
    return kindLabel(kind);
  }

  protected iconForKind(kind: string): string {
    return kindIcon(kind);
  }
}
